import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { differenceInCalendarDays } from 'date-fns';

import AccountLayout from '../../components/account-layout';
import Alerts from '../../components/alerts';
import Backdrop from '../../components/backdrop';
import { useTranslation } from '../../hooks/useTranslation';
import colors from '../../themes/main-colors';
import MultiAccountSummary from '../../components/account-summary/multi-accounts/MultiAccountSummary';
import AccountDropdown from '../../components/account-dropdown';
import SingleAccountSummary from '../../components/account-summary/single-account/SingleAccountSummary';
import TextLink from '../../components/text-link';
import ROUTES from '../../routes';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import {
  IQBD_BANNER_DISMISSALS_STORAGE_KEY,
  removeFromStorage,
  setInStorage,
  useStoredState,
} from '../../util/storage-utils';
import { useLocation } from '@reach/router';
import { getSelectedAccount } from '../../components/account-dropdown/queries';
import useAuthQuery from '../../hooks/useAuthQuery';
import { featureFlagsContext } from '../../providers/FeatureFlagsProvider';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import ReconnectServiceSuccessNotice from '../../components/reconnect-service/reconnect-service-success-notice';
import useAccountsStore from '../../hooks/useAccountsStore';
import { PublicSafetyPowerShutoffDetails } from '../../components/public-safety-power-shutoff/PublicSafetyPowerShutoffDetails';
import AlertBanner from '../../components/AlertBanner';
import EnrollIntoTwoStepAuthNotice from '../../components/two-step-authentication/EnrollIntoTwoStepAuthNotice';
import {
  AccountDetail,
  AccountType,
  IqbdStatusInfo,
} from '../../__generated__/pge-types';
import { getIQBDStatus } from '../../components/account-summary/single-account/queries';
import { LanguageContext } from '../../providers/LanguageProvider';
import { parseLocalDate } from '../../util/date-utils';

export const SELECTED_ACCOUNT_STORAGE_KEY =
  'selected-account-from-summary-page';
const FORCE_MULTI_KEY = 'forceShowMultiSummary';

type IqbdBannerPhase = '60-day' | '30-day' | 'expired';

const getIqbdBannerPhase = (
  iqbdStatus?: IqbdStatusInfo | null,
): IqbdBannerPhase | undefined => {
  if (!iqbdStatus?.expirationDate) {
    return undefined;
  }

  const daysUntilExpiration = differenceInCalendarDays(
    parseLocalDate(iqbdStatus.expirationDate),
    new Date(),
  );

  if (
    iqbdStatus.isExpired &&
    daysUntilExpiration >= -30 &&
    daysUntilExpiration <= 0
  ) {
    return 'expired';
  }

  if (!iqbdStatus.isEnrolled || !iqbdStatus.isSameAccountEnrolled) {
    return undefined;
  }

  if (daysUntilExpiration >= 0 && daysUntilExpiration <= 30) {
    return '30-day';
  }

  if (daysUntilExpiration >= 31 && daysUntilExpiration <= 60) {
    return '60-day';
  }

  return undefined;
};

const formatIqbdExpirationDate = (expirationDate: string, language: string) => {
  const date = parseLocalDate(expirationDate);
  const month = new Intl.DateTimeFormat(language, { month: 'long' }).format(
    date,
  );

  return `${month} ${date.getDate()}, ${date.getFullYear()}`;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainArea: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    section: {
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    backLink: {
      '& a': {
        display: 'flex',
        alignItems: 'center',
        '&:active': {
          borderBottom: 'none',
        },
      },
      marginBottom: theme.spacing(1),
    },
    iqbdBanner: {
      '& .MuiAlertTitle-root': {
        fontWeight: 'bold',
        marginBottom: 0,
      },
      '& a': {
        color: colors.white,
        textDecoration: 'underline',
      },
    },
  }),
);

type Props = {
  path?: string;
};

export default function AccountSummaryPage(_: Props) {
  const {
    accountParams,
    encryptedAccountNumber,
    encryptedPersonId,
    automaticSelection,
    selectedGroupId,
    setSelectedAccountParams,
  } = useSelectedAccountParams();
  const location = useLocation();

  const { t, richT, inlineRichT } = useTranslation();
  const classes = useStyles();
  const { customer, error, loading } = useAccountCustomer();
  const [isAccountDisonnected, setIsAccountDisonnected] = useState<boolean>(
    false,
  );
  const { customerAccountState } = useAccountsStore();
  const [
    isAccountEligibleForReconnect,
    setIsAccountEligibleForReconnect,
  ] = useState<boolean>(false);
  const [isReconnectInProgress, setIsReconnectInProgress] = useState<boolean>(
    false,
  );
  const [isLastPaymentDateGreater, setIsLastPaymentDateGreater] = useState<
    boolean
  >(false);
  const [forceShowMulti, setForceShowMulti] = useStoredState(
    FORCE_MULTI_KEY,
    false,
  );
  const [iqbdBannerDismissals, setIqbdBannerDismissals] = useStoredState<
    Record<string, boolean>
  >(IQBD_BANNER_DISMISSALS_STORAGE_KEY, {});
  const totalAccounts = customer?.accountMeta?.totalAccounts || 0;
  const showSingle =
    (totalAccounts <= 1 || !automaticSelection) && !forceShowMulti;
  const hasInactiveAccounts = customerAccountState.hasInactiveAccounts;
  const notificationContext = useContext(NotificationsContext);
  const { language } = useContext(LanguageContext);
  const { displayMyAccountInfoBanner } = useContext(featureFlagsContext);
  const { isOutagesPSPSEnabled } = useContext(featureFlagsContext);
  const { data } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getSelectedAccount, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams || loading,
  });

  const selectedAccount = data?.getAccountDetails?.[0];
  const encryptedSaIds = selectedAccount?.premiseInfo
    ?.flatMap(premise => premise?.saDetails?.map(sa => sa?.encryptedSAId) ?? [])
    .filter(Boolean) as string[] | undefined;
  const isIqbdQueryEligible = Boolean(
    showSingle &&
      accountParams &&
      encryptedSaIds?.length &&
      selectedAccount?.accountNumber === accountParams.accountNumber &&
      selectedAccount?.accountType === AccountType.Res,
  );

  const { data: iqbdData, loading: iqbdLoading } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getIQBDStatus, {
    variables: {
      iqbdParams: { encryptedSaIds },
      params: { accountNumberList: [accountParams] },
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    skip: !isIqbdQueryEligible,
  });
  const iqbdStatus =
    isIqbdQueryEligible && !iqbdLoading
      ? iqbdData?.getAccountDetails?.[0]?.IQBDStatus
      : undefined;
  const iqbdBannerPhase = getIqbdBannerPhase(iqbdStatus);
  const normalizedEmail = customer?.email.trim().toLowerCase();
  const iqbdBannerDismissalToken =
    iqbdBannerPhase &&
    iqbdStatus?.expirationDate &&
    accountParams?.accountNumber &&
    normalizedEmail
      ? JSON.stringify([
          normalizedEmail,
          accountParams.accountNumber,
          iqbdStatus.expirationDate,
          iqbdBannerPhase,
        ])
      : undefined;
  const showIqbdBanner = Boolean(
    isIqbdQueryEligible &&
      !iqbdLoading &&
      iqbdBannerPhase &&
      iqbdBannerDismissalToken &&
      !iqbdBannerDismissals[iqbdBannerDismissalToken],
  );

  const dismissIqbdBanner = () => {
    if (iqbdBannerDismissalToken) {
      setIqbdBannerDismissals(dismissals => ({
        ...dismissals,
        [iqbdBannerDismissalToken]: true,
      }));
    }
  };

  const { OMSdowntime } = useContext(featureFlagsContext);

  //handle energy use menu link for multiple account
  //if the url has hash of #energyUser we modify the forceShowMulti variable
  useEffect(() => {
    if (location.hash === '#energyUse') {
      selectedAccount &&
      selectedGroupId &&
      totalAccounts && // check if total  account value is not zero
      totalAccounts > 1 && // set selected account only for multi user with multi account
        setSelectedAccountParams(selectedGroupId, selectedAccount); // who selected account value is not set
      setForceShowMulti(false); // by setting flag to false we redirect user to single summary page
    }
  }, [location, data]);

  useEffect(() => {
    if (displayMyAccountInfoBanner) {
      notificationContext.setState({
        isOpen: true,
        message: t('MYACCOUNT_INFO_BANNER'),
        severity: 'info',
      });
    }
  }, [displayMyAccountInfoBanner]);

  useEffect(() => {
    if (!loading && !showSingle) {
      removeFromStorage(SELECTED_ACCOUNT_STORAGE_KEY);
    } else if (accountParams?.accountNumber) {
      setInStorage(SELECTED_ACCOUNT_STORAGE_KEY, accountParams?.accountNumber);
    }
  }, [loading, showSingle, accountParams]);

  if (!loading && !showSingle) {
    return (
      <>
        <EnrollIntoTwoStepAuthNotice />
        <MultiAccountSummary
          onSelectAccount={() => {
            setForceShowMulti(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      {isReconnectInProgress &&
        isAccountDisonnected &&
        isLastPaymentDateGreater && <ReconnectServiceSuccessNotice />}

      <EnrollIntoTwoStepAuthNotice />

      <AccountLayout dense={false} showSingle={showSingle}>
        <Helmet>
          <title>{t('ACCOUNT_SUMMARY')}</title>
        </Helmet>

        <div className={classes.mainArea}>
          {error !== undefined && (
            <Alerts
              isOpen
              severity={'error'}
              variant={'standard'}
              title={t('ACCOUNT_SUMMARY_ERROR')}
              message={error.toString()}
            />
          )}

          {loading && <Backdrop forceOpen />}

          <Grid container direction="column" spacing={3} wrap="nowrap">
            {!loading && (
              <>
                <Grid item>
                  {totalAccounts > 1 && (
                    <Typography
                      className={`${classes.backLink} ${classes.colorTextPrimary}`}
                      variant={'h4'}
                    >
                      <TextLink
                        onClick={async e => {
                          e.preventDefault();
                          setForceShowMulti(true);
                          window.scrollTo(0, 0);
                        }}
                        to={ROUTES.ACCOUNT}
                      >
                        <ChevronLeft />
                        {t('BACK_TO_ACCOUNT_LIST')}
                      </TextLink>
                    </Typography>
                  )}
                  <Typography
                    className={classes.colorTextPrimary}
                    variant={'h1'}
                  >
                    {t('MY_ACCOUNT_DASHBOARD')}
                  </Typography>
                </Grid>

                {showIqbdBanner && (
                  <Grid item md={12}>
                    <div className={classes.iqbdBanner}>
                      <Alerts
                        isOpen
                        severity={
                          iqbdBannerPhase === 'expired' ? 'error' : 'warning'
                        }
                        variant={'filled'}
                        title={
                          iqbdBannerPhase === 'expired' &&
                          iqbdStatus?.expirationDate
                            ? inlineRichT('IQBD_EXPIRED_WARNING_BANNER', {
                                expirationDate: formatIqbdExpirationDate(
                                  iqbdStatus.expirationDate,
                                  language,
                                ),
                                iqbdFormUrl: ROUTES.IQBD_FORM,
                              })
                            : inlineRichT('IQBD_EXPIRATION_WARNING_BANNER', {
                                iqbdFormUrl: ROUTES.IQBD_FORM,
                              })
                        }
                        onClose={dismissIqbdBanner}
                      />
                    </div>
                  </Grid>
                )}

                <Grid item md={12}>
                  {showSingle && <AccountDropdown />}
                </Grid>
              </>
            )}

            {!loading &&
              isOutagesPSPSEnabled &&
              showSingle &&
              accountParams && (
                <PublicSafetyPowerShutoffDetails
                  isAccountSelected={showSingle}
                  accountParams={[accountParams]}
                  selectedAccount={data?.getAccountDetails?.[0]}
                />
              )}

            {OMSdowntime && (
              <Grid item>
                <AlertBanner
                  icon={false}
                  title={t('OMS_DOWN_TIME_TITLE')}
                  text={richT('OMS_DOWN_TIME_CUSTOMER_DASHBOARD')}
                />
              </Grid>
            )}

            {!loading && showSingle && (
              <SingleAccountSummary
                accountParams={accountParams}
                encryptedAccountNumber={encryptedAccountNumber}
                encryptedPersonId={encryptedPersonId}
                iqbdStatus={iqbdStatus}
                setIsDisonnected={setIsAccountDisonnected}
                setIsEligibleForReconnect={setIsAccountEligibleForReconnect}
                setIsReconnectInProgress={setIsReconnectInProgress}
                setIsLastPaymentDateGreater={setIsLastPaymentDateGreater}
              />
            )}
          </Grid>
        </div>
      </AccountLayout>
    </>
  );
}
